import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const DEFAULT_SERVICE = {
  name: "",
  active_days: [0, 1, 2, 3, 4, 5, 6],
  start_time: "18:00",
  end_time: "23:00",
  table_duration_min: 120,
  reservation_interval_min: 30,
  max_online_guests: 60,
  room_ids: [],
  lounge_enabled: true,
  reservation_types_enabled: ["dinner", "birthday", "romantic", "business", "family", "event"],
  allergies_required: false,
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_SERVICE);

  const load = async () => {
    const [s1, s2] = await Promise.all([api.get("/services"), api.get("/rooms")]);
    setServices(s1.data);
    setRooms(s2.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...DEFAULT_SERVICE, room_ids: rooms.map((r) => r.room_id) });
    setShow(true);
  };

  const openEdit = (svc) => {
    setEditing(svc.service_id);
    setForm({ ...DEFAULT_SERVICE, ...svc });
    setShow(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/services/${editing}`, form);
      else await api.post("/services", form);
      toast.success("Service gespeichert");
      setShow(false);
      load();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Service löschen?")) return;
    await api.delete(`/services/${id}`);
    load();
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      active_days: f.active_days.includes(d) ? f.active_days.filter((x) => x !== d) : [...f.active_days, d].sort(),
    }));
  };

  const toggleRoom = (id) => {
    setForm((f) => ({
      ...f,
      room_ids: f.room_ids.includes(id) ? f.room_ids.filter((x) => x !== id) : [...f.room_ids, id],
    }));
  };

  const daysShort = t("services.days_short");

  return (
    <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="services-title">{t("services.title")}</h1>
        </div>
        <button onClick={openNew} data-testid="services-new-btn" className="btn-primary text-xs inline-flex items-center gap-2"><Plus className="w-3.5 h-3.5" />{t("services.new")}</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.length === 0 && (
          <div className="surface-1 rounded-md p-12 text-center text-sm text-[var(--text-dim)] col-span-full" data-testid="services-empty">{t("services.empty")}</div>
        )}
        {services.map((svc) => (
          <div key={svc.service_id} data-testid={`service-${svc.service_id}`} className="surface-1 rounded-md p-6 hover:-translate-y-0.5 transition-all duration-300 group cursor-pointer" onClick={() => openEdit(svc)}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-heading text-xl font-light">{svc.name}</h3>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(svc.service_id); }} data-testid={`service-delete-${svc.service_id}`} className="text-[var(--text-dim)] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="font-heading text-2xl text-gold mb-4">{svc.start_time} – {svc.end_time}</div>
            <div className="flex gap-1 mb-4">
              {daysShort.map((d, i) => (
                <span key={i} className={`text-[10px] w-7 h-7 rounded flex items-center justify-center ${svc.active_days?.includes(i) ? "bg-gold/20 text-gold border border-gold/30" : "bg-white/5 text-[var(--text-dim)]"}`}>{d}</span>
              ))}
            </div>
            <div className="text-xs text-[var(--text-muted)] space-y-1">
              <div className="flex justify-between"><span>Dauer</span><span>{svc.table_duration_min} Min.</span></div>
              <div className="flex justify-between"><span>Intervall</span><span>{svc.reservation_interval_min} Min.</span></div>
              <div className="flex justify-between"><span>Max. Online</span><span>{svc.max_online_guests}</span></div>
              <div className="flex justify-between"><span>Lounge</span><span>{svc.lounge_enabled ? "Ja" : "Nein"}</span></div>
            </div>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6 overflow-y-auto" onClick={() => setShow(false)}>
          <div className="surface-1 rounded-md w-full max-w-2xl p-8 my-12" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-light mb-6">{editing ? "Service bearbeiten" : t("services.new")}</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <Field label={t("services.name")}>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" className="input" placeholder="Dinner" />
              </Field>
              <Field label={t("services.active_days")}>
                <div className="flex gap-2">
                  {daysShort.map((d, i) => (
                    <button key={i} type="button" onClick={() => toggleDay(i)} data-testid={`service-day-${i}`} className={`w-10 h-10 rounded text-xs ${form.active_days.includes(i) ? "bg-gold text-black" : "bg-white/5 text-[var(--text-muted)]"}`}>{d}</button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("services.start_time")}>
                  <input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="service-start-input" className="input [color-scheme:dark]" />
                </Field>
                <Field label={t("services.end_time")}>
                  <input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="service-end-input" className="input [color-scheme:dark]" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("services.table_duration")}>
                  <input type="number" min="30" max="360" value={form.table_duration_min} onChange={(e) => setForm({ ...form, table_duration_min: parseInt(e.target.value) })} className="input" />
                </Field>
                <Field label={t("services.interval")}>
                  <select value={form.reservation_interval_min} onChange={(e) => setForm({ ...form, reservation_interval_min: parseInt(e.target.value) })} className="input">
                    <option value="15">15 Min.</option>
                    <option value="30">30 Min.</option>
                    <option value="60">60 Min.</option>
                  </select>
                </Field>
              </div>
              <Field label={t("services.max_online")}>
                <input type="number" min="0" max="999" value={form.max_online_guests} onChange={(e) => setForm({ ...form, max_online_guests: parseInt(e.target.value) })} className="input" />
              </Field>
              {rooms.length > 0 && (
                <Field label="Räume">
                  <div className="flex flex-wrap gap-2">
                    {rooms.map((r) => (
                      <button key={r.room_id} type="button" onClick={() => toggleRoom(r.room_id)} className={`px-3 py-1.5 rounded-full text-xs ${form.room_ids.includes(r.room_id) ? "bg-gold/20 text-gold border border-gold/40" : "bg-white/5 text-[var(--text-muted)] border border-white/10"}`}>{r.name}</button>
                    ))}
                  </div>
                </Field>
              )}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] cursor-pointer">
                  <input type="checkbox" checked={form.lounge_enabled} onChange={(e) => setForm({ ...form, lounge_enabled: e.target.checked })} className="accent-gold" />
                  {t("services.lounge_enabled")}
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] cursor-pointer">
                  <input type="checkbox" checked={form.allergies_required} onChange={(e) => setForm({ ...form, allergies_required: e.target.checked })} className="accent-gold" />
                  {t("services.allergies_required")}
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShow(false)} className="btn-ghost text-xs flex-1">{t("common.cancel")}</button>
                <button type="submit" data-testid="service-submit" className="btn-primary text-xs flex-1">{t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;background:#141414;border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:0 16px;height:44px;font-size:14px;color:#F5F5F0;outline:none;}.input:focus{border-color:#D4AF37;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[var(--text-muted)] mb-2 block uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
